import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import HeroSection from '../../components/Kpathport/PathFeed/HeroSection';
import WorkshopGrid from '../../components/Kpathport/PathFeed/WorkshopGrid';
import ReviewSection from '../../components/Kpathport/PathFeed/ReviewSection';
import Pagination from '../../components/Kpathport/PathFeed/Pagination';
import SuccessNotificationToast from '../../components/Kpathport/Modal/SuccessNotificationModal';
import AlreadyInBagModal from '../../components/Kpathport/Modal/AlreadyInBagModal';
import ItemUnavailableModal from '../../components/Kpathport/Modal/ItemUnavailableModal';
import {
  BaseButton,
  BREAKPOINTS,
  useAuth,
  useMediaQuery,
  useT,
  trackWorkshopStepStart,
} from '@repo/common';
import { getWorkshopList } from '../../api/workshop/workshop.api';
import {
  getWorkshopCategory,
  getWorkshopCategoryLabel,
} from '../../components/Kpathport/utils/workshopCategory';
import { KPATHPORT_ROUTES } from '../../components/Kpathport/utils/routes';
import { addToCart } from '../../api/cart/cart.api';
import {
  addWorkshopLike,
  getWorkshopLikeStatus,
  removeWorkshopLike,
} from '../../api/workshop/workshopLike.api';

const CARD_IMAGE = `${process.env.REACT_APP_ASSETS_URL}/Kpathport/img-kpathport-cardimg.svg`;
const DownArrowImage = `${process.env.REACT_APP_ASSETS_URL}/Kpathport/img-kpathport-downarrow.svg`;
const UnderlineImage = `${process.env.REACT_APP_ASSETS_URL}/Kpathport/img-kpathport-underline.svg`;

const PathFeed = ({ category: categoryProp }) => {
  const t = useT();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // 구 ?step=1 / ?step=01 링크 호환: step 1 → Culture, 2 → Career, 그 외 → etc
  const legacyStep = searchParams.get('step');
  const legacyStepNumber = Number(legacyStep);
  const legacyCategory = !legacyStep
    ? null
    : legacyStepNumber === 1
      ? 'culture'
      : legacyStepNumber === 2
        ? 'career'
        : 'etc';
  // 카테고리가 지정되지 않은 /pathfeed 는 모든 카테고리(CULTURE/CAREER/ETC)의 워크샵을 보여준다.
  // /culture, /career, /etc 처럼 카테고리가 지정된 경우에만 해당 카테고리로 필터링한다.
  const requestedCategory =
    categoryProp ?? searchParams.get('category') ?? legacyCategory;
  const categoryInfo = getWorkshopCategory(requestedCategory);
  const categoryValue = categoryInfo?.value ?? null; // null → 전체
  const isInvalidCategory = Boolean(requestedCategory) && !categoryInfo;

  useEffect(() => {
    if (isInvalidCategory) {
      navigate(KPATHPORT_ROUTES.PATHFEED, { replace: true });
    }
  }, [isInvalidCategory, navigate]);
  const [allWorkshops, setAllWorkshops] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [wishlistMap, setWishlistMap] = useState({}); // { workshopId: boolean }
  const [showAddedToBagToast, setShowAddedToBagToast] = useState(false);
  const [showAlreadyInBagModal, setShowAlreadyInBagModal] = useState(false);
  const [showItemUnavailableModal, setShowItemUnavailableModal] =
    useState(false);
  const { isLoggedIn, user } = useAuth();
  const isAdmin = ['A', 'ROLE_A', 'ADMIN', 'ROLE_ADMIN'].includes(
    String(user?.role ?? '').toUpperCase(),
  );
  const isTabletOrLarger = useMediaQuery(
    `(min-width: ${BREAKPOINTS.tabletH}px)`,
  );
  const isDesktop = useMediaQuery(`(min-width: ${BREAKPOINTS.desktopL}px)`);
  const workshopsPerPage = isDesktop ? 15 : isTabletOrLarger ? 10 : 5;
  const featuredWorkshopCount = isDesktop ? 9 : isTabletOrLarger ? 6 : 5;

  useEffect(() => {
    if (isInvalidCategory) return;

    trackWorkshopStepStart({
      step_number: categoryValue ?? 'ALL',
      step_name: categoryInfo
        ? categoryInfo.title.toLowerCase().replace(/\s+/g, '_')
        : 'all_workshops',
      source: searchParams.get('source') || 'nav_menu',
    });
  }, [categoryValue, isInvalidCategory]);

  useEffect(() => {
    if (isInvalidCategory) return;

    const fetchWorkshops = async () => {
      try {
        setLoading(true);

        // 카테고리가 지정되면 해당 카테고리만, 아니면 전체 워크샵 조회
        const all = await getWorkshopList(
          categoryValue ? { category: categoryValue } : {},
        );

        // 데이터 변환
        const transformWorkshop = (workshop) => {
          const tags = [];
          if (workshop.hit) tags.push({ label: 'Hit', tone: 'accent' });
          if (workshop.free) tags.push({ label: 'Free', tone: 'neutral' });

          return {
            id: workshop.id,
            title: workshop.title || '',
            description: workshop.description || '',
            keywords: workshop.keywords || [],
            tags,
            image: workshop.thumbnailImage || CARD_IMAGE,
            featured: workshop.featured || false,
            ctaLabel: 'Quick add',
            stepNumber: workshop.category,
            stepName: getWorkshopCategoryLabel(workshop.category),
          };
        };

        const transformedWorkshops = all.map(transformWorkshop);
        setAllWorkshops(transformedWorkshops);

        // 초기 wishlist 상태 설정
        const initialWishlistMap = {};
        if (isLoggedIn && transformedWorkshops.length > 0) {
          const likeResults = await Promise.all(
            transformedWorkshops.map(async (workshop) => {
              try {
                const liked = await getWorkshopLikeStatus(workshop.id);
                return [workshop.id, !!liked];
              } catch (error) {
                return [workshop.id, false];
              }
            }),
          );

          likeResults.forEach(([id, liked]) => {
            initialWishlistMap[id] = liked;
          });
        } else {
          transformedWorkshops.forEach((workshop) => {
            initialWishlistMap[workshop.id] = false;
          });
        }
        setWishlistMap(initialWishlistMap);
      } catch (error) {
        console.error('워크샵 목록 조회 실패:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkshops();
  }, [isLoggedIn, categoryValue, isInvalidCategory]);

  // 화면 크기가 바뀌면 해당 레이아웃의 첫 페이지부터 다시 보여준다.
  useEffect(() => {
    setCurrentPage(1);
  }, [categoryValue, workshopsPerPage]);

  const getResponsivePageWorkshops = () => {
    const startIndex = (currentPage - 1) * workshopsPerPage;
    const pageItems = allWorkshops.slice(
      startIndex,
      startIndex + workshopsPerPage,
    );

    if (currentPage === 1 && isTabletOrLarger) {
      return {
        firstSection: pageItems.slice(0, featuredWorkshopCount),
        remainingSection: pageItems.slice(featuredWorkshopCount),
      };
    }

    return {
      firstSection: pageItems,
      remainingSection: [],
    };
  };

  const totalPages = Math.max(
    1,
    Math.ceil(allWorkshops.length / workshopsPerPage),
  );
  const { firstSection, remainingSection } = getResponsivePageWorkshops();

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Wishlist 토글 핸들러
  const handleToggleWishlist = async (workshopId, isWishlisted) => {
    setWishlistMap((prev) => ({
      ...prev,
      [workshopId]: isWishlisted,
    }));

    try {
      if (isWishlisted) {
        await addWorkshopLike(workshopId);
      } else {
        await removeWorkshopLike(workshopId);
      }
    } catch (error) {
      console.error('Failed to toggle workshop like:', error);
      setWishlistMap((prev) => ({
        ...prev,
        [workshopId]: !isWishlisted,
      }));
    }
  };

  // 장바구니 추가 핸들러
  const handleDateSelected = async (workshopDateSlotId, selectedSlot) => {
    try {
      await addToCart(workshopDateSlotId, 1);
      // 성공 토스트 표시
      setShowAddedToBagToast(true);
    } catch (error) {
      console.error('Failed to add to cart:', error);

      // 중복 에러인 경우 모달 표시
      const errorCode = error.response?.data?.code;
      const errorMessage = error.response?.data?.message || error.message;
      const statusCode = error.response?.status;

      // 409 상태 코드이거나 에러 메시지에 "이미 장바구니에 담긴"이 포함된 경우
      const isAlreadyInBag =
        statusCode === 409 ||
        errorCode === 409 ||
        errorCode === 'CART_ITEM_ALREADY_EXISTS' ||
        errorMessage?.includes('이미 장바구니에 담긴') ||
        errorMessage?.includes('already exists');

      if (isAlreadyInBag) {
        setShowAlreadyInBagModal(true);
        return;
      }

      // 시스템/타이밍 에러인 경우
      const isSystemError =
        statusCode === 500 ||
        errorCode === 500 ||
        errorMessage?.includes('시스템') ||
        errorMessage?.includes('system') ||
        errorMessage?.includes('timing');

      if (isSystemError) {
        setShowItemUnavailableModal(true);
        return;
      }

      // 기타 에러는 alert로 표시
      alert(
        errorMessage ||
          '장바구니에 추가하는데 실패했습니다. 다시 시도해주세요.',
      );
    }
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <p>로딩 중...</p>
      </div>
    );
  }

  return (
    <div className='bg-grayscale-gray01 pb-[100px] tabletH:pb-[320px]'>
      <HeroSection isAdmin={isAdmin} />

      {isAdmin && (
        <div className='flex justify-end px-6 pb-5 pt-6 tabletH:px-10 desktopL:px-20'>
          <BaseButton
            type='primary'
            size='m'
            hideIcon
            typo={false}
            onClick={() => navigate('/admin/workshop/new')}
            className='h-auto min-w-0 rounded-lg border-0 bg-green-deep02 px-5 py-3 typo-b14Strong text-white hover:bg-green-deep02 hover:opacity-90'
          >
            + Create workshop
          </BaseButton>
        </div>
      )}

      {/* 첫 페이지일 때만 "Book now" 섹션 표시 */}
      {firstSection.length > 0 && (
        <section className='flex flex-col px-6 pt-5 tabletH:px-10 tabletH:pt-0 desktopL:px-20'>
          {currentPage === 1 && (
            <div className='relative flex flex-col items-start pb-[30px] text-start'>
              {/* Mobile Decorative Elements */}
              <div className='block tabletH:hidden absolute left-[2px] top-5 w-[68.309px] h-[21.849px]'>
                <div className='flex-none rotate-[7.38deg]'>
                  <div className='h-[13.334px] relative w-[67.153px]'>
                    {/* Decorative vector - placeholder for actual image */}
                  </div>
                </div>
              </div>
              <div className='block tabletH:hidden absolute left-[116.64px] top-[-16.29px] w-[41.404px] h-[49.021px]'>
                {/* Decorative elements - placeholder for actual images */}
              </div>

              <div className='relative flex flex-col items-start text-start'>
                <div className='relative inline-flex items-center'>
                  <h2 className='whitespace-nowrap typo-title1 text-text-primary tabletV:typo-h7 desktopL:typo-h5'>
                    {t('Book now', '지금 예약하기')}
                  </h2>
                  <img
                    src={DownArrowImage}
                    alt=''
                    aria-hidden='true'
                    className='absolute -right-8 -top-3 h-[41px] w-[29px] tabletH:-right-12 tabletH:-top-4 tabletH:h-[49px] tabletH:w-[41px] desktopL:-right-[58px] desktopL:-top-3 desktopL:h-[63px] desktopL:w-[51px]'
                  />
                </div>
                <img
                  src={UnderlineImage}
                  alt=''
                  aria-hidden='true'
                  className='absolute -bottom-3 left-0 h-[13px] w-[67px] tabletH:h-[19px] tabletH:w-[94px] desktopL:h-[22px] desktopL:w-[111px]'
                />
              </div>
            </div>
          )}

          <WorkshopGrid
            items={firstSection.map((item) => ({
              ...item,
              isWishlisted: wishlistMap[item.id] || false,
              onToggleWishlist: (isWishlisted) =>
                handleToggleWishlist(item.id, isWishlisted),
              onDateSelected: handleDateSelected,
              stepNumber: item.stepNumber,
              stepName: item.stepName,
            }))}
          />

          {remainingSection.length === 0 &&
            !(currentPage === 1 && isTabletOrLarger) && (
              <Pagination
                current={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
        </section>
      )}

      {/* 첫 페이지일 때만 리뷰 섹션 표시 (모바일에서는 숨김) */}
      {currentPage === 1 && isTabletOrLarger && firstSection.length > 0 && (
        <div className='mt-[60px] desktopL:mt-20'>
          <ReviewSection workshops={allWorkshops} />
          {remainingSection.length === 0 && (
            <div className='mt-[50px] px-10 desktopL:px-20'>
              <Pagination
                current={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      )}

      {/* 나머지 워크샵 섹션 */}
      {remainingSection.length > 0 && (
        <section className='mt-[60px] flex flex-col gap-[50px] px-10 desktopL:mt-20 desktopL:px-20'>
          <WorkshopGrid
            items={remainingSection.map((item) => ({
              ...item,
              isWishlisted: wishlistMap[item.id] || false,
              onToggleWishlist: (isWishlisted) =>
                handleToggleWishlist(item.id, isWishlisted),
              onDateSelected: handleDateSelected,
              stepNumber: item.stepNumber,
              stepName: item.stepName,
            }))}
          />
          <Pagination
            current={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </section>
      )}

      {/* Added to Bag Toast */}
      <SuccessNotificationToast
        isOpen={showAddedToBagToast}
        onClose={() => setShowAddedToBagToast(false)}
        type='bag'
      />

      {/* Already in Bag Modal */}
      <AlreadyInBagModal
        isOpen={showAlreadyInBagModal}
        onClose={() => setShowAlreadyInBagModal(false)}
      />

      {/* Item Unavailable Modal */}
      <ItemUnavailableModal
        isOpen={showItemUnavailableModal}
        onClose={() => setShowItemUnavailableModal(false)}
      />
    </div>
  );
};

export default PathFeed;
